import { useMemo } from "react";
import { type AgGrid, Flex, Text } from "@jsq/ds/core";
import { personsColumn, sourceDocumentColumn, TaxOverviewSection } from "./TaxOverviewSection";
import { type FatcaOverviewRow } from "./utils";

export type FatcaOverviewSectionProps = {
  caseId: number;
  rows: FatcaOverviewRow[];
  onAddDocument: (row: FatcaOverviewRow) => void;
  onOpenPersons: (row: FatcaOverviewRow) => void;
  readonly?: boolean;
};

const NameCell = ({ data }: AgGrid.ICellRendererParams<FatcaOverviewRow>) =>
  data ? (
    <Flex flexDirection="column" gap={2}>
      <Text variant="regular-md">{data.rootProfileName}</Text>
      <Text variant="secondary-sm">{data.positionsLabel}</Text>
    </Flex>
  ) : null;

const nameColumn: AgGrid.ColDef<FatcaOverviewRow> = {
  field: "rootProfileName",
  headerName: "Name",
  minWidth: 432,
  cellRenderer: NameCell,
};

export const FatcaOverviewSection = ({
  caseId,
  rows,
  onAddDocument,
  onOpenPersons,
  readonly = false,
}: FatcaOverviewSectionProps) => {
  const columnDefs = useMemo<AgGrid.ColDef<FatcaOverviewRow>[]>(
    () => [
      nameColumn,
      sourceDocumentColumn<FatcaOverviewRow>(
        caseId,
        "fatcaSourceDocument",
        "FATCA source document",
        onAddDocument,
        readonly,
      ),
      personsColumn<FatcaOverviewRow>(
        "fatcaSubstantialOwner",
        "FATCA substantial owner",
        onOpenPersons,
      ),
    ],
    [caseId, onAddDocument, onOpenPersons, readonly],
  );
  return (
    <TaxOverviewSection
      title="FATCA Overview"
      helpText="Describes the FATCA source documents assigned to positions associated with this profile and the individuals identified as Substantial US Owners based on the investor's tax document. FATCA source document is profile-specific and applicable across all positions."
      rows={rows}
      columnDefs={columnDefs}
    />
  );
};
